import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import { GuideLayout, GuideWay } from './GuideStepPanel';
import { PARTS_WAYS, PARTS_SHOT } from './stepGuides';

/**
 * Parts step: how to add parts + a picker that opens an existing open work order
 * straight into the add-part dialog (?addPart=1).
 */
function PartsStepPanel({ openWorkOrders, onOpenWorkOrder, onCreateWorkOrder, onConnectSuppliers }) {
  const { t } = useTranslation();
  const workOrders = useMemo(() => openWorkOrders || [], [openWorkOrders]);
  const [selectedId, setSelectedId] = useState(workOrders[0]?.id ?? '');

  useEffect(() => {
    if (workOrders.length && !workOrders.some((workOrder) => workOrder.id === selectedId)) {
      setSelectedId(workOrders[0].id);
    }
  }, [workOrders, selectedId]);

  const selected = workOrders.find((workOrder) => workOrder.id === selectedId);

  const describe = (workOrder) => {
    const parts = [workOrder.registration_number, workOrder.vehicle].filter(Boolean);
    return parts.join(' · ');
  };

  return (
    <GuideLayout shot={PARTS_SHOT} shotAlt={t('onboardingChecklist.steps.parts.title')}>
      <Typography sx={{ fontSize: '13.5px', lineHeight: 1.6 }}>
        {t('onboardingChecklist.steps.parts.guide.intro')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {PARTS_WAYS.map((way, index) => (
          <GuideWay
            key={index}
            icon={way.icon}
            title={t(`onboardingChecklist.steps.parts.guide.ways.${index}.title`)}
            description={t(`onboardingChecklist.steps.parts.guide.ways.${index}.desc`)}
          />
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.75,
          border: '1px solid',
          borderColor: 'rgba(49, 130, 206, 0.35)',
          bgcolor: 'rgba(49, 130, 206, 0.04)',
        }}
      >
        {workOrders.length > 0 ? (
          <>
            <Typography sx={{ fontSize: '12.5px', fontWeight: 700, mb: 1 }}>
              {t('onboardingChecklist.steps.parts.pickerLabel')}
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
              sx={{ mb: 1.25, bgcolor: 'background.paper' }}
              inputProps={{ 'data-testid': 'onboarding-parts-workorder-select' }}
            >
              {workOrders.map((workOrder) => (
                <MenuItem key={workOrder.id} value={workOrder.id}>
                  <Box sx={{ display: 'flex', gap: 1, minWidth: 0, alignItems: 'baseline' }}>
                    {workOrder.work_number && (
                      <Box component="span" sx={{ fontWeight: 700, flexShrink: 0 }}>
                        {workOrder.work_number}
                      </Box>
                    )}
                    <Box
                      component="span"
                      sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {describe(workOrder)}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              startIcon={<AddShoppingCartIcon />}
              endIcon={<ArrowForwardIcon />}
              onClick={() => selected && onOpenWorkOrder(selected.id)}
              sx={{ textTransform: 'none' }}
              data-testid="onboarding-parts-open-workorder"
            >
              {t('onboardingChecklist.steps.parts.openCta', {
                workNumber: selected?.work_number || selected?.registration_number || '',
              })}
            </Button>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: 0.875, textAlign: 'center' }}>
              {t('onboardingChecklist.steps.parts.openHint')}
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '12.5px', fontWeight: 700, mb: 1 }}>
              {t('onboardingChecklist.steps.parts.noWorkOrders')}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={onCreateWorkOrder}
              sx={{ textTransform: 'none' }}
            >
              {t('onboardingChecklist.steps.workorder.guide.cta')}
            </Button>
          </>
        )}
      </Paper>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <LinkIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 160 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
            {t('onboardingChecklist.steps.parts.suppliersTitle')}
          </Typography>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
            {t('onboardingChecklist.steps.parts.suppliersDesc')}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={onConnectSuppliers}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          {t('onboardingChecklist.steps.parts.suppliersCta')}
        </Button>
      </Box>
    </GuideLayout>
  );
}

export default PartsStepPanel;
